import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styles from "./style.module.css";
import LoadContent from "components/LoadContent";
import langData from "lang/data";
import { selectMain } from "store/selector";
import FromToPage from "components/FromToPage";
import PageSeo from "components/PageSeo";
import { getFeatureCategories } from "../../../../store/HomeSlice";
import { categories } from "store/WsSearchSlice";
import getImageUrl from "helpers/getImageUrl";

/**
 * Category cards must show PHOTO (category_images), never menu SVG icons.
 * Same shape as home feature-categories-cat: category_image.image
 */
function resolveCategoryImagePath(item) {
  if (!item || typeof item !== "object") return null;
  const photo =
    item?.category_image?.image ||
    item?.image?.image ||
    (typeof item?.category_image === "string" ? item.category_image : null) ||
    (typeof item?.image === "string" && !String(item.image).includes("/icons/")
      ? item.image
      : null);
  if (!photo) return null;
  // Never use catalog icon SVGs as the card image.
  if (/\/icons\//i.test(photo) || /\.svg(\?|$)/i.test(photo)) return null;
  return photo;
}

const SearchCategory = () => {
  const location = useLocation();
  const WSsearchInfo = location?.state?.similar;
  const category = location?.state?.category;
  const { featureCategories } = useSelector(({ home }) => home);
  const { categoriesWs } = useSelector(({ WsSearchSlice }) => WsSearchSlice);
  const [searchParams] = useSearchParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentLanguage } = useSelector(selectMain);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(getFeatureCategories());
      setLoading(false);
    };
    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (!WSsearchInfo?.length) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [WSsearchInfo, searchParams]);

  useEffect(() => {
    const encodedCategoryIds = searchParams.get("ct");
    if (!encodedCategoryIds) return;
    try {
      const categoryIds = JSON.parse(atob(encodedCategoryIds));
      // categoriesWs is an object `{ categories: [...] }`, not an array.
      if (!categoriesWs?.categories?.length) {
        setLoading(false);
        dispatch(categories(categoryIds));
      }
    } catch (error) {
      navigate(
        `${currentLanguage !== "hy" ? "/" + currentLanguage : ""}/search-null`
      );
    }
  }, [currentLanguage, searchParams, categoriesWs?.categories?.length, dispatch, navigate]);

  const allCategory = useMemo(() => {
    // Prefer API refetch when it has rows (includes icon after backend fix).
    if (categoriesWs?.categories?.length) {
      return categoriesWs.categories;
    }
    if (WSsearchInfo?.length) {
      return category ? [category, ...WSsearchInfo] : WSsearchInfo;
    }
    const encodedCategoryIds = searchParams.get("ct");
    if (!encodedCategoryIds) {
      return featureCategories;
    }
    return [];
  }, [
    categoriesWs?.categories,
    WSsearchInfo,
    category,
    searchParams,
    featureCategories,
  ]);

  return (
    <>
      <PageSeo pageKey="categories" />
      <div className="wrapper">
        {loading ? (
          <LoadContent containerClassName={styles.logLoad} />
        ) : (
          <>
            <FromToPage type={true} />
            <div className={styles.title}></div>
          </>
        )}
        <div className={styles.brand}>
          <div className={styles.brandTop}>
            {loading ? (
              <LoadContent containerClassName={styles.leftLoad} />
            ) : (
              <div className={styles.brandLeft}>
                <div className={styles.brandLeftContainer}>
                  <h1>{langData.brand.category[currentLanguage]}</h1>
                  <div className={styles.brandLeftList}>
                    {allCategory?.map((item) => (
                      <span
                        key={item?.id ?? item?.slug}
                        onClick={() =>
                          navigate(
                            `${
                              currentLanguage !== "hy"
                                ? "/" + currentLanguage
                                : ""
                            }/category/filter/${item?.slug}`
                          )
                        }
                      >
                        {item?.["name_" + currentLanguage]}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {loading ? (
              <LoadContent containerClassName={styles.rightLoad} />
            ) : (
              <div className={styles.brandRight}>
                {allCategory?.map((item) => {
                  const imgSrc = getImageUrl(resolveCategoryImagePath(item));
                  return (
                    <div key={item?.id} className={styles?.brandRightItem}>
                      <div
                        className={styles.brandRightItemTop}
                        onClick={() =>
                          navigate(
                            `${
                              currentLanguage !== "hy"
                                ? "/" + currentLanguage
                                : ""
                            }/category/filter/${item?.slug}`
                          )
                        }
                      >
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={
                              item?.["name_" + currentLanguage] ||
                              item?.name_en ||
                              "category"
                            }
                            onError={(e) => {
                              e.currentTarget.style.visibility = "hidden";
                            }}
                          />
                        ) : null}
                      </div>
                      <div className={styles.brandRightItemBottom}>
                        <span
                          onClick={() =>
                            navigate(
                              `${
                                currentLanguage !== "hy"
                                  ? "/" + currentLanguage
                                  : ""
                              }/category/filter/${item?.slug}`
                            )
                          }
                        >
                          {item?.["name_" + currentLanguage]}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchCategory;
